import 'reactApp/swiper.scss';

import sha1 from 'js-sha1';
import { ReactComponent as EditIcon } from 'mrg-icons/src/mailru/actions/compose.svg';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import { openEditor } from 'reactApp/appHelpers/appHelpers';
import { IS_B2B_BIZ_USER, IS_BLOCKED, IS_PUBLIC_ALBUM } from 'reactApp/appHelpers/configHelpers';
import { isTouchSearchEnabled } from 'reactApp/appHelpers/featuresHelpers';
import { isViewContentOnly } from 'reactApp/appHelpers/settingsHelpers';
import { getEditorsFor } from 'reactApp/modules/editor/editor.selectors';
import { getFacesFileId } from 'reactApp/modules/faces/faces.helpers';
import { getFacesIdxOnFile, getFacesList } from 'reactApp/modules/faces/faces.selectors';
import { getMyOfficeTouchFeature } from 'reactApp/modules/features/features.selectors';
import { getWeblinkFromPublicId, isArchive, isDocument, isPreviewable } from 'reactApp/modules/file/utils';
import { getCurrentRouteId, getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getSearchRequestParams } from 'reactApp/modules/search/search.selectors';
import { SearchFile } from 'reactApp/modules/search/search.types';
import { changeStorageHistory, getStorage } from 'reactApp/modules/storage/storage.helpers';
import { getStorageCurrentFolder } from 'reactApp/modules/storage/storage.selectors';
import { CloudFile, EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { selectItemIndexInArray } from 'reactApp/modules/viewer/viewer.module';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import { RootState } from 'reactApp/store';
import { sendViewerDwh as sendViewerMobileDwh } from 'reactApp/ui/Mobile/MobileViewer/MobileViewer.helpers';
import { MobileViewerItem } from 'reactApp/ui/Mobile/MobileViewer/MobileViewerItem/MobileViewerItem';
import { ViewerToolbar } from 'reactApp/ui/Mobile/MobileViewer/ViewerToolbar/ViewerToolbar';
import { renderMobileOptionsModal } from 'reactApp/ui/Mobile/OptionsModal/OptionsModal.helpers';
import { sendViewerDwh } from 'reactApp/ui/ReactViewer/ReactViewer.helpers';
import { ViewerButton } from 'reactApp/ui/ReactViewer/ViewerButton/ViewerButton';
import { SwipingDownComponent } from 'reactApp/ui/SwipingDownComponent/SwipingDownComponent';
import { createGaSender } from 'reactApp/utils/ga';
import { noop } from 'reactApp/utils/helpers';
import { ECategoryGa, EPaymentGa } from 'reactApp/utils/paymentGa';
import { scrollLock, scrollUnlock } from 'reactApp/utils/scrollLock';
import SwiperCore, { Virtual, Zoom } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import styles from './MobileViewer.css';

SwiperCore.use([Zoom, Virtual]);

const GA_CATEGORY = 'mobile-viewer';
const sendViewerGa = createGaSender(GA_CATEGORY);

const SWIPER_SPEED = 350;

interface Props {
    onClose: () => void;
}

const virtualOptions = {
    addSlidesBefore: 3,
    addSlidesAfter: 3,
};

const animationClasses = {
    exit: styles.toolbar_exit,
    exitActive: styles.toolbar_exit_active,
    enter: styles.toolbar_enter,
    enterActive: styles.toolbar_enter_active,
};

export const MobileViewer = memo<Props>(
    // eslint-disable-next-line max-lines-per-function
    ({ onClose = noop }) => {
        const dispatch = useDispatch();

        const itemId = useSelector(ViewerSelectors.getViewerItemId);
        let storage = useSelector(getCurrentStorage);
        const { isHome, isPublic, isStock, isGallery, isAttaches, isSearch } = getStorage(storage);
        if (isAttaches) {
            storage = EStorageType.viewerAttaches;
        }
        const itemsIdsList = useSelector(ViewerSelectors.getViewerItemIds) || [];
        const itemIndex = useSelector(ViewerSelectors.getViewerItemIndexInArray) || 0;
        const totalCount = useSelector(ViewerSelectors.getViewerItemCount);
        const currentItem = useSelector(ViewerSelectors.getViewerItem) as CloudFile;
        const folder = useSelector((state: RootState) => getStorageCurrentFolder(state, storage));
        const myOfficeTouchFeature = useSelector(getMyOfficeTouchFeature);
        const isEditable = !!useSelector((state: RootState) => getEditorsFor(state, currentItem))?.length;
        const isProUser = useSelector(UserSelectors.isProUser);
        const isPaidUser = useSelector(UserSelectors.isPaidUser);

        const hasMyOfficeFeature = myOfficeTouchFeature && isDocument(currentItem);
        const showMyOfficeEditButton = isEditable && hasMyOfficeFeature && myOfficeTouchFeature.editablePages.includes(storage);

        const [swiper, setSwiper] = useState<SwiperCore | null>(null);
        const [showCustomItem, setShowCustomItem] = useState(false);
        const [showToolbar, setShowToolbar] = useState<boolean>(!showCustomItem && !isViewContentOnly);
        const [isZoom, setIsZoom] = useState(false);
        const [swipeCount, setSwiperCount] = useState<number>(0);
        const [slideTransition, setSlideTransition] = useState<boolean>(false);

        const timerId = useRef<number | undefined>(undefined);
        const currentRoute = useSelector(getCurrentRouteId);

        /**
         * После обратноно маунта тулбара после анмаунта, руками показываем тулбар,
         * иначе он отобразиться только если потапать на экран
         */
        useEffect(() => {
            if (!showCustomItem && !isViewContentOnly) {
                setShowToolbar(true);
            }
        }, [showCustomItem, isViewContentOnly]);

        const rootRef = useRef<null | any>(null);

        const ext = (!currentItem?.isFolder && currentItem?.ext?.toLowerCase()) || '';
        const nameWithoutExt = (!currentItem?.isFolder && currentItem?.nameWithoutExt) || '';

        const fileId = getFacesFileId(itemId || '');
        const facesIdx = useSelector((state: RootState) => getFacesIdxOnFile(state, fileId));
        const faces = useSelector((state: RootState) => getFacesList(state));
        const { query, xPageId, xReqId } = useSelector(getSearchRequestParams);

        const sendDwh = useCallback(
            ({ action, source, ...rest }) => {
                const touchSource = IS_PUBLIC_ALBUM ? 'album' : source || storage;

                sendViewerDwh({
                    action,
                    id_media: sha1(currentItem?.id || ''),
                    type_content: currentItem?.kind,
                    extension: ext,
                    source: `touch-${touchSource}`,
                    is_stories: false,
                    id_public: isPublic && currentItem?.id ? getWeblinkFromPublicId(currentItem?.id) : 'None',
                    ...rest,
                });

                if (storage === EStorageType.search) {
                    const item = currentItem as SearchFile;
                    sendViewerDwh({
                        eventCategory: ECategoryGa.viewer_search,
                        action,
                        count_media: 0,
                        have_faces: Boolean(faces?.length),
                        id_media: sha1(item?.id || ''),
                        type_content: item?.kind,
                        extension: item?.isFolder ? 'None' : ext,
                        is_archive: isArchive(item),
                        is_edit: isEditable,
                        is_full_render: isPreviewable(item),
                        place: 'touch',
                        section: 'search',
                        is_stories: false,
                        id_public: isPublic && item?.id ? getWeblinkFromPublicId(item?.id) : 'None',
                        name_files: item?.name,
                        search_phrase: query,
                        page_id: xPageId,
                        req_id: xReqId,
                        pos: item?.pos,
                        hash: item?.hash,
                        size: item?.size,
                        ...rest,
                    });
                }
            },
            [storage, currentItem, ext, isPublic, faces?.length, isEditable, query, xPageId, xReqId]
        );

        const closeViewer = useCallback(
            (noHistoryChange = false) => {
                onClose();
                if (!isGallery && !noHistoryChange) {
                    changeStorageHistory({ id: folder?.id, storage });
                }
                sendViewerMobileDwh({
                    action: EPaymentGa.close,
                    item: currentItem,
                    storage,
                });
            },
            [folder?.id, onClose, storage]
        );

        useEffect(() => {
            sendViewerGa('open', '', { count: itemsIdsList.length });

            sendDwh({ action: 'open' });

            if (currentItem.storage === EStorageType.search) {
                const file = currentItem as SearchFile;

                sendDwh({
                    action: 'open',
                    forceSend: true,
                    eventCategory: ECategoryGa.entered,
                    free_user: !isPaidUser,
                    paid_user: isPaidUser,
                    pro_user: isProUser,
                    name_files: file?.name,
                    pos: file?.pos,
                    hash: file?.hash,
                    mtime: file?.mtime,
                    size: file?.size,
                    search_phrase: query,
                    page_id: xPageId,
                    req_id: xReqId,
                    kind: file?.kind,
                    type: file?.ext,
                });
            }

            const el = rootRef?.current;

            scrollLock(el, { reserveScrollBarGap: false, allowTouchMove: true });

            return () => scrollUnlock(el);
        }, []);

        useEffect(() => {
            if (IS_B2B_BIZ_USER) {
                sendViewerGa(`b2b-${storage}`, IS_BLOCKED ? 'blocked' : 'nonbl');
            }
        }, [storage]);

        /**
         *  Когда открыли вьювер на 500+ фотографии и перезагрузили страницу.
         *  Скролимся до нужного элемента.
         */
        useEffect(() => {
            if (itemsIdsList.length === 0) {
                closeViewer();
                return;
            }

            if (swiper?.activeIndex !== itemIndex) {
                swiper?.virtual?.update(true);
                swiper?.slideTo(itemIndex, SWIPER_SPEED);
            }
        }, [itemsIdsList.length, closeViewer, swiper, itemIndex]);

        // менять слайд, если нажали браузерную кнопку назад
        useEffect(() => {
            /**
             * TODO: убрать после починки роутера. на хомяки и стоках в currentRoute приходит не полный путь:
             * Вместо /папка/файл.jpg приходить только /папка.
             * Из-за этого вся логика ниже работает некорректно.
             */
            if ((isTouchSearchEnabled && isSearch) || isHome || isStock || isViewContentOnly) {
                return;
            }

            // У элементов галерее нет своего пути.
            if (isGallery) {
                return;
            }

            if (itemId === currentRoute) {
                return;
            }

            const routeIndex = itemsIdsList.indexOf(currentRoute);

            if (routeIndex !== -1 && swiper) {
                swiper.slideTo(routeIndex, SWIPER_SPEED);
            } else {
                closeViewer();
            }
        }, [closeViewer, currentRoute, isViewContentOnly]);

        useEffect(() => {
            // Сменился сторадж - мы куда-то перешли и надо закрыть просмотрщик
            if (storage !== currentItem?.storage) {
                closeViewer(true);
            }
        }, [storage, currentItem, closeViewer]);

        useEffect(() => {
            if (currentItem && swiper) {
                if (currentItem.kind !== 'image') {
                    swiper.zoom.disable();
                } else {
                    swiper.zoom.enable();
                }
            }
        }, [currentItem]);

        useEffect(() => {
            if (isViewContentOnly) {
                sendViewerGa('cnt-only', 'ext', { [ext]: 1 });
                sendViewerGa('cnt-only', 'stor', { [storage.replace(EStorageType.viewerAttaches, EStorageType.attaches)]: 1 });
            }
        }, [isViewContentOnly, ext, storage]);

        const handleClose = useCallback(
            (label: string) => {
                if (isViewContentOnly) {
                    return;
                }

                sendViewerGa('close', label);
                closeViewer();

                sendDwh({ action: 'close' });
            },
            [closeViewer, sendDwh, isViewContentOnly]
        );

        const handleSlideChange = useCallback(
            (swiper: SwiperCore) => {
                const id = itemsIdsList[swiper.activeIndex];

                // если открыть просмотрщик не на первой фотке swiperJs вызывает slideChange, в этом случае отправлять радар не надо
                if (swipeCount !== 0 || itemId !== id) {
                    sendViewerGa('swipe', '', { swipeCount: swipeCount + 1 });
                    setSwiperCount((prevCount) => prevCount + 1);

                    sendDwh({
                        action: 'skip-photo',
                        type_click:
                            // Событие точно есть, но SwiperCore, описывает не все типы
                            (swiper as SwiperCore & { swipeDirection: 'next' | 'prev' }).swipeDirection === 'next' ? 'next' : 'previous',
                    });
                }

                dispatch(selectItemIndexInArray({ itemIndex: swiper.activeIndex }));
            },
            [dispatch, itemId, sendDwh]
        );

        const handleSlideTouchStart = useCallback(() => {
            setSlideTransition(true);
        }, []);

        const handleSlideTouchEnd = useCallback(() => {
            setSlideTransition(false);
        }, []);

        const handleSlideChangeEnd = useCallback(
            (swiper) => {
                const id = itemsIdsList[swiper.activeIndex];
                if (!isGallery && !isAttaches && id) {
                    changeStorageHistory({ id, storage });
                    setIsZoom(false);
                }
            },
            [storage]
        );

        const handleZoom = useCallback((_: SwiperCore, scale: number): void => {
            sendViewerGa('zoom');
            setShowToolbar(false);

            if (scale === 1) {
                setIsZoom(false);
            } else {
                setIsZoom(true);
            }
        }, []);

        const toggleToolbar = useCallback(() => {
            if (!timerId.current) {
                const id = window.setTimeout(() => {
                    setShowToolbar((prevValue) => !prevValue);
                    timerId.current = undefined;
                }, 500);
                timerId.current = id;
            }
        }, [timerId, setShowToolbar]);

        const handleOnDoubleClick = useCallback(() => {
            window.clearTimeout(timerId.current);
            timerId.current = undefined;
        }, [timerId]);

        const onInitialSwiper = useCallback((swiper: SwiperCore) => {
            setSwiper(swiper);
        }, []);

        const closeOnSwipe = useCallback(() => {
            handleClose('swipe');
        }, [handleClose]);

        const openToolbarOptions = useCallback(() => {
            renderMobileOptionsModal({ id: itemId, storage, onlyActions: true, sendAnalytics: sendDwh, skipRename: true });
        }, [itemId, sendDwh, storage]);

        const isLockSwipingDown = useMemo(() => {
            return isViewContentOnly || isZoom || showCustomItem || currentItem?.kind === 'pdf' || isAttaches;
        }, [isZoom, showCustomItem, currentItem, isAttaches, isViewContentOnly]);

        // Запрещаем переключение слайдов на документах через мой офис
        const allowSlideNavigation = !hasMyOfficeFeature && !isViewContentOnly;

        return (
            <div className={styles.root} ref={rootRef}>
                <SwipingDownComponent onClose={closeOnSwipe} lock={isLockSwipingDown}>
                    <div className={styles.content}>
                        {!showCustomItem && (
                            <CSSTransition in={showToolbar} timeout={SWIPER_SPEED} unmountOnExit exit enter classNames={animationClasses}>
                                <ViewerToolbar
                                    onClose={handleClose}
                                    ext={ext}
                                    nameWithoutExt={nameWithoutExt}
                                    noReturn={isAttaches}
                                    onClickOption={openToolbarOptions}
                                    subTitle={`${itemIndex + 1} из ${totalCount}`}
                                    itemId={itemId}
                                    faces={facesIdx}
                                />
                            </CSSTransition>
                        )}
                        <div className={styles.swiperWrapper}>
                            <Swiper
                                onSwiper={onInitialSwiper}
                                initialSlide={itemIndex}
                                spaceBetween={20}
                                onSlideChange={isViewContentOnly ? noop : handleSlideChange}
                                zoom
                                allowSlideNext={allowSlideNavigation}
                                allowSlidePrev={allowSlideNavigation}
                                onZoomChange={handleZoom}
                                onDoubleClick={isViewContentOnly ? noop : handleOnDoubleClick}
                                onClick={isViewContentOnly ? noop : toggleToolbar}
                                simulateTouch={false}
                                onTouchStart={handleSlideTouchStart}
                                onTouchEnd={handleSlideTouchEnd}
                                onSlideChangeTransitionEnd={handleSlideChangeEnd}
                                slidesPerView={1}
                                virtual={virtualOptions}
                                resizeObserver={false}
                                speed={150}
                            >
                                {itemsIdsList.map((item, index) => {
                                    return (
                                        <SwiperSlide key={item} zoom virtualIndex={index}>
                                            <MobileViewerItem
                                                openToolbarOptions={openToolbarOptions}
                                                setShowCustomItem={setShowCustomItem}
                                                selected={itemId === item}
                                                slideTransition={slideTransition}
                                                itemId={item}
                                                storage={storage}
                                                sendAnalytics={sendDwh}
                                                isViewContentOnly={isViewContentOnly}
                                                isPublic={isPublic}
                                            />
                                        </SwiperSlide>
                                    );
                                })}
                            </Swiper>
                        </div>
                        {showMyOfficeEditButton && (
                            <div className={styles.controls}>
                                <ViewerButton
                                    title="Редактировать документ"
                                    onClick={() => openEditor(currentItem)}
                                    icon={<EditIcon />}
                                    qaName="edit"
                                />
                            </div>
                        )}
                    </div>
                </SwipingDownComponent>
            </div>
        );
    }
);

MobileViewer.displayName = 'MobileViewer';
